import { Inject, Injectable } from '@angular/core';
import { GOOGLE_ANALYTICS_GTAG_TOKEN, GOOGLE_ANALYTICS_SETTINGS_TOKEN, WINDOW_TOKEN } from './constants';
import { IGoogleAnalyticsSettings } from './interfaces/IGoogleAnalyticsSettings';
import { GAAction, GTagFn } from './type';

@Injectable({ providedIn: "root" })
export class GoogleAnalyticsService {
    constructor(
        @Inject(GOOGLE_ANALYTICS_SETTINGS_TOKEN) private readonly settings: IGoogleAnalyticsSettings,
        @Inject(WINDOW_TOKEN) private readonly window: Window,
        @Inject(GOOGLE_ANALYTICS_GTAG_TOKEN) private readonly gtag: GTagFn
    ){}

    private toKeyValue(map: Map<string, any>): {[key: string]: any}{
        return (map.size > 0)
            ? Array.from(map).reduce((obj, [key, value]) => Object.defineProperty(obj, key, { value, enumerable: true }), {})
            : undefined
    }

    private isModuleActive(){
        return Boolean(this.settings.trackingCode);
    }

    /** Active ou désactive toutes les requêtes de Google Analytics */
    enableAnalytics(value: boolean){
        if(this.window)
            this.window[`ga-disable-${this.settings.trackingCode}`] = !value;
    }

    /** Active ou désactive l'anonymisation des adresses IP : https://support.google.com/analytics/answer/2763052?hl=fr
     * A une conséquence sur la précision des statistiques des villes, appareil, utilisateurs, sessions et taux de rebond.
     */
    anonymizeIP(value: boolean){
        this.settings.anonymizeIp = value;
        this.gtag("config", this.settings.trackingCode, { 'anonymize_ip': value });
    }

    /**
     * Send an event to google analytics
     * https://developers.google.com/analytics/devguides/collection/gtagjs/events
     * @param action The value that will appear as the event action in Google Analytics Event reports.
     * @param category The category of the event.
     * @param label The label of the event.
     * @param value A non-negative integer that will appear as the vent value.
     * @param interaction This parameter allows you to determine how you want bounce rate defined for pages on your site that also include event measurement.
     */
    event(action: GAAction, category?: string, label?: string, value?: number, interaction?: boolean){
        if(!this.isModuleActive()) return;
        let _opt = new Map<string, any>();
        if(category) _opt.set("event_category", category);
        if(label) _opt.set("event_label", label);
        if(value) _opt.set("value", value);
        if(interaction !== undefined) _opt.set("interaction", interaction);
        let params = this.toKeyValue(_opt);
        if(params){
            this.gtag("event", action, params);
        } else {
            this.gtag("event", action);
        }
    }

    /**
     * Send page_view event
     * https://developers.google.com/analytics/devguides/collection/gtagjs/pages
     * @param path The path to the page. If overridden, this value must start with a / character.
     * @param title The title of the page.
     * @param location The URL of the page
     */
    pageView(path: string, title?: string, location?: string){
        if(!this.isModuleActive()) return;
        let _opt = new Map<string, any>();
        _opt.set("page_path", path);
        _opt.set("send_to", this.settings.trackingCode);
        if(!title) title = window.document.title;
        if(!location) location = window.document.location.href;
        if(title) _opt.set("page_title", title);
        if(location) _opt.set("page_location", location);
        let params = this.toKeyValue(_opt);
        this.gtag("event", "page_view", params);
    }
}